<script setup lang="ts">
import { type RouteLocationNormalizedLoaded, RouterView } from 'vue-router'
import {
  dateZhCN,
  deDE,
  enUS,
  frFR,
  type GlobalThemeOverrides,
  itIT,
  jaJP,
  NConfigProvider,
  NDialogProvider,
  NGlobalStyle,
  NMessageProvider,
  NNotificationProvider,
  zhCN,
  zhTW
} from 'naive-ui'
import { computed, onMounted, ref, watch } from 'vue'
import router from './router'
import { useGlobalStore } from './stores/global'
import RouterTransition from './components/base/RouterTransition.vue'
import { downloadApp, isSupportBrowser, isElectron } from '@/utils/common'
import { IosClose } from '@vicons/ionicons4'
import Bowser from 'bowser'
import { I18nKeys } from './constants/i18n'
import { useI18n } from 'vue-i18n'
import i18n, { setupI18n } from '@/i18n'
import { checkNetwork } from '@/apis/common'
import { EmitKeys, IS_INTL } from '@/constants/global'
import { useBus, useMemberLevel } from '@/compositions/global'
import metrics from './utils/metrics'
import { useAccountStore } from './stores/account'
import { MemberStatus, type MemberInfo } from './typings/account'
import { getProfile, getMemberInfo, getPromotions } from './apis/account'
import { UserPromotions } from './constants/account'
import CookieNotice from './components/base/CookieNotice.vue'
import { generateDeviceFingerprint } from './utils/fingerprint'
import { GA4Event } from './typings/base'
import AboutDialog from './components/base/AboutDialog.vue'
import { getFbclid } from './apis/campaign'
import { getIpFingerprint } from './utils/fingerprint/ip'
import { getActionByUrl, getClidByIpAction, getActionByFingerprint } from './utils/action'
import { useTrigger } from './compositions/trigger'

// 取消文字选中
// document.addEventListener('selectstart', (e) => {
//   e.preventDefault()
// })

const { t } = useI18n()
const locale = ref(zhCN)
const lang = i18n.global.locale
const initLang = () => {
  if (lang.value === 'zh-cn') {
    locale.value = zhCN
  } else if (lang.value === 'zh-hant') {
    locale.value = zhTW
  } else if (lang.value === 'ja') {
    locale.value = jaJP
  } else if (lang.value === 'fr') {
    locale.value = frFR
  } else if (lang.value === 'de') {
    locale.value = deDE
  } else if (lang.value === 'it') {
    locale.value = itIT
  } else {
    locale.value = enUS
  }
}
watch(
  () => lang.value,
  () => initLang(),
  { immediate: true }
)

// 禁用触摸板缩放页面
document.addEventListener(
  'wheel',
  (e) => {
    if (e.ctrlKey && e.deltaY !== 0) {
      e.preventDefault()
    }
  },
  { passive: false }
)

// 阻止双指放大
document.addEventListener(
  'touchmove',
  (event: TouchEvent) => {
    // @ts-ignore
    if (event.scale !== 1) {
      event.preventDefault()
    }
    // 在 console 页面时阻止右滑手势
    if (router.currentRoute.value.name === 'console') {
      const touch = event.touches[0]
      const startX = touch.clientX
      if (startX < 50) {
        // 从屏幕左边缘开始的滑动
        event.preventDefault()
      }
    }
  },
  { passive: false }
)

// 文件拖拽交互
// 计数器用于跟踪进入和离开元素的次数
let dragCounter = 0
// 获取最外层的元素
const outerElement = document.getElementById('app') as HTMLElement
const bus = useBus()
// 监听文件拖拽进入最外层元素的事件
outerElement.addEventListener('dragenter', (event: DragEvent) => {
  event.preventDefault()
  // 如果是图片文件才出发下面逻辑
  if (event.dataTransfer?.types.includes('Files')) {
    // 仅当进入的是最外层元素时，才增加计数器
    if (dragCounter === 0) {
      // 在这里添加视觉效果
      bus.emit(EmitKeys.IMAGE_DRAG_START)
    }
    dragCounter++
  }
})
// 监听文件拖拽离开最外层元素的事件
outerElement.addEventListener('dragleave', (event: DragEvent) => {
  event.preventDefault()
  // 如果是图片文件才出发下面逻辑
  if (event.dataTransfer?.types.includes('Files')) {
    dragCounter--
    // 仅当完全离开最外层元素时，才触发离开的逻辑
    if (dragCounter === 0) {
      // 在这里移除视觉效果
      bus.emit(EmitKeys.IMAGE_DRAG_END)
    }
  }
})
// 阻止默认的拖拽行为
outerElement.addEventListener('dragover', (event: DragEvent) => {
  event.preventDefault()
})
// 监听文件放下的事件
outerElement.addEventListener('drop', (event: DragEvent) => {
  event.preventDefault()
  dragCounter = 0 // 重置计数器
  // 这里可以处理文件的放置
  bus.emit(EmitKeys.IMAGE_DRAG_END)
})

const globalStore = useGlobalStore()

// const currentTheme = computed(() => globalStore.currentTheme)

// 主题设置
const themeOverrIdes: GlobalThemeOverrides = {
  common: {
    primaryColor: '#6BF218',
    primaryColorPressed: '#7EF235',
    primaryColorHover: '#C9F2AF',
    primaryColorSuppl: '#75f21a',
    borderColor: '#E3EAF1',
    bodyColor: '#ffffff',
    fontWeightStrong: '700',
    textColorBase: '#0D0D0D',
    textColor1: '#777777',
    textColor2: '#4c4c4c',
    textColor3: '#757575',
    actionColor: '#5AC4F2',
    hoverColor: '#f2f2f2'
  },
  Button: {
    textColorPrimary: '#0D0D0D',
    textColorHoverPrimary: '#0D0D0D',
    textColorPressedPrimary: '#060D03',
    textColorFocusPrimary: '#0D0D0D',
    textColorDisabledPrimary: '#15161B',
    heightTiny: '24px',
    borderRadiusLarge: '10px',
    borderRadiusMedium: '8px',
    borderRadiusSmall: '6px',
    borderRadiusTiny: '4px',
    textColorHover: '#4c4c4c',
    textColorFocus: '#4c4c4c',
    textColorPressed: '#4c4c4c'
  },
  Popover: {
    textColor: '#0D0D0D',
    space: '12px',
    padding: '8px'
  },
  Spin: {
    opacitySpinning: 0.3
  },
  Scrollbar: {
    color: '#7E807D96',
    colorHover: '#7E807D96'
  },
  Slider: {
    handleColor: '#808080',
    handleSize: '14px',
    fillColor: '#808080',
    fillColorHover: '#808080',
    dotBorderActive: '2px solid #808080',
    dotBorder: '2px solid #E6E6E6',
    dotWidth: '14px',
    dotHeight: '14px',
    dotBorderRadius: '8px',
    dotColor: '#E6E6E6',
    markFontSize: '12px',
    fontSize: '12px'
  },
  Input: {
    caretColor: '#000000',
    borderRadius: '10px',
    color: '#f2f2f2',
    colorFocus: '#f2f2f2'
  },
  Checkbox: {
    sizeLarge: '24px',
    border: '1px solid #cccccc',
    borderChecked: '1px solid #cccccc',
    borderFocus: '1px solid #cccccc',
    colorChecked: '#00000000',
    checkMarkColor: '#4c4c4c',
    boxShadowFocus: 'none'
  },
  Switch: {
    railWidthMedium: '44px'
  },
  Dropdown: {
    optionTextColor: '#7E807D'
  },
  Dialog: {
    titleTextColor: '#000000',
    titleFontSize: '20px',
    titleFontWeight: 600
  },
  Divider: {
    color: '#e6e6e6'
  },
  Carousel: {
    dotColor: '#B0B2AE',
    dotColorActive: '#80EAFF'
  }
}

const showBrowserTips = ref<boolean>(false)
// 当前项目
const currentProject = computed(() => globalStore.currentProject)
const setTitle = (route: RouteLocationNormalizedLoaded) => {
  // 设置页面标题
  if (route.name === 'login') {
    document.title = `${t(I18nKeys.global.signupTitle)} | ${t(I18nKeys.global.signupTitleSuffix)}`
  } else if (route.name === 'desktop') {
    document.title = t(I18nKeys.global.recentsTitle)
  } else if (route.name === 'console' && currentProject.value) {
    document.title = t(I18nKeys.global.projectNameTitle, {
      projectName: currentProject.value.name
    })
  } else {
    document.title = t(I18nKeys.global.indexTitle)
  }
}
watch(
  () => router.currentRoute.value,
  (route: RouteLocationNormalizedLoaded) => {
    globalStore.setCurrentViewName(route.name as string)
    setTitle(route)
  },
  {
    immediate: true
  }
)
watch(
  () => lang.value,
  () => setTitle(router.currentRoute.value),
  { immediate: true }
)

// 下载APP
const download = () => {
  downloadApp()
  metrics.event(GA4Event.DOWNLOAD_APP, { method: globalStore.envInfo.os }).fire()
}

// APP自动更新
const updateAvailable = computed({
  get: () => globalStore.updateAvailable,
  set: (available: boolean) => globalStore.setUpdateAvailable(available)
})
const checkUpdateCallback = () => {
  updateAvailable.value = true
}

const setCssProperty = () => {
  let vh = window.innerHeight * 0.01
  document.documentElement.style.setProperty('--vh', `${vh}px`)
  document.documentElement.style.setProperty(
    '--console-height',
    `calc(calc(100 * ${vh}px) - 50px${isElectron() ? ' - 32px' : ''})`
  )
  document.documentElement.style.setProperty(
    '--background-1',
    'linear-gradient(120deg, #5AC4F2 0%, #6BF218 56%)'
  )
  document.documentElement.style.setProperty(
    '--background-2',
    'linear-gradient(120deg, #ECEF63 0%, #6BF218 56%)'
  )
  document.documentElement.style.setProperty(
    '--background-3',
    'linear-gradient(120deg, #ECEF63 0%, #AEF224 100%)'
  )
  document.documentElement.style.setProperty(
    '--background-4',
    'linear-gradient(120deg, #AFF528 0%, #6BF218 100%)'
  )
  document.documentElement.style.setProperty(
    '--background-5',
    'linear-gradient(180deg, #F1F2C2 0%, #FFFFFF 32%)'
  )
  document.documentElement.style.setProperty(
    '--background-6',
    'linear-gradient(131deg, #EEF218 0%, #AEF224 100%)'
  )
  document.documentElement.style.setProperty(
    '--background-7',
    'linear-gradient(120deg, #EEF218 0%, #7EF235 100%)'
  )
  document.documentElement.style.setProperty(
    '--background-8',
    'linear-gradient(90deg, #AEF224 0%, #7EF235 100%)'
  )
  document.documentElement.style.setProperty(
    '--background-9',
    'linear-gradient(90deg, #EEF218 0%, #EEF218 100%)'
  )
  document.documentElement.style.setProperty('--tools-width', '300px')
  document.documentElement.style.setProperty('--text-color-4', '#B0B2AE')
}

// 检测网络
const networkAvailable = computed(() => globalStore.networkAvailable)
const serverAvailable = computed(() => globalStore.serverAvailable)
const showNetworkTips = computed(() => {
  return (
    !networkAvailable.value &&
    router.currentRoute.value.name !== 'network-error' &&
    router.currentRoute.value.name !== 'update' &&
    router.currentRoute.value.name !== 'update-error' &&
    router.currentRoute.value.name !== 'update-progress'
  )
})
watch([() => showNetworkTips.value, () => serverAvailable.value], ([networkTips, server]) => {
  if (!networkTips) {
    bus.emit(EmitKeys.SHOW_NETWORK_ERROR)
  }

  if (!server) {
    bus.emit(EmitKeys.SHOW_NETWORK_ERROR)
  }
})

// 刷新用户信息
const accountStore = useAccountStore()
const { authEnterpriseEdition } = useMemberLevel()
const memberInfo = computed({
  get: () => accountStore.memberInfo,
  set: (member: MemberInfo | null) => accountStore.setMemberInfo(member)
})
const getUserPromotions = async () => {
  if (authEnterpriseEdition()) return

  const promotions = await getPromotions()
  if (Array.isArray(promotions) && promotions.length > 0) {
    promotions.forEach((promotion) => {
      if (promotion.key === UserPromotions.USER_FIRST_SIGN_REWARD_TOAST) {
        accountStore.setMemberStatus(MemberStatus.ONE_DAY)
      }
    })
    if (
      !promotions.some(
        (promotion) => promotion.key === UserPromotions.USER_FIRST_SIGN_REWARD_TOAST
      ) &&
      !memberInfo.value?.userMember
    ) {
      accountStore.setMemberStatus(MemberStatus.EXPIRED)
    }
  } else if (!memberInfo.value?.userMember) {
    accountStore.setMemberStatus(MemberStatus.EXPIRED)
  }
}
const refreshUserInfo = async () => {
  if (router.currentRoute.value.name === 'login') return

  console.info('刷新用户信息')
  // getUserProfile
  const profile = await getProfile()
  accountStore.setNickname(profile.nickname)
  accountStore.setAvatar(profile.avatar)
  accountStore.setUserId(profile.suid)

  // getMember
  memberInfo.value = await getMemberInfo()
  await getUserPromotions()
}

// 获取用户同意状态
const showConsentTips = ref<boolean>(false)
const showCookiePolisyDialog = ref<boolean>(false)
const setConsentState = (state: string) => {
  const expirationDate = new Date()
  expirationDate.setFullYear(expirationDate.getFullYear() + 50)
  const cookieString = `metrics_consent=${state}; domain=${
    import.meta.env.VITE_COOKIE_DOMAIN
  }; path=/; expires=${expirationDate.toUTCString()}`
  document.cookie = cookieString
}
const getConsentState = () => {
  const consent = document.cookie.split('; ').find((row) => row.startsWith('metrics_consent='))
  return consent ? consent.split('=')[1] : null
}
const acceptConsent = () => {
  setConsentState(metrics.consentGranted())
  showConsentTips.value = false
}

const initFirebase = () => {
  import('./utils/firebase')
}

const initApmPlus = () => {
  import('./utils/apmplus')
}

const showAboutDialog = ref<boolean>(false)

// 如果没有 _fbc 或者 _gcl_aw，则尝试通过 ip 获取 clid 并设置到 cookie 中，应对用户在移动端打开了广告但未注册，但是直接在 PC 端访问的情况
const getClidByIp = async () => {
  const ipInfo = await getIpFingerprint()
  if (!ipInfo || !ipInfo.ipAddress) return

  const fbCookie = document.cookie.split('; ').find((row) => row.startsWith('_fbc='))
  const gbCookie = document.cookie.split('; ').find((row) => row.startsWith('_gcl_aw='))
  if (fbCookie || gbCookie) return

  const { fbclid } = await getFbclid(ipInfo.ipAddress)
  if (fbclid) {
    if (fbclid.startsWith('fb.')) {
      // Facebook 归因
      const fbc = `fb.1.${new Date().getTime()}.${fbclid.split('.')[1]}`
      document.cookie = `_fbc=${fbc}; domain=${
        import.meta.env.VITE_COOKIE_DOMAIN
      }; path=/; expires=Fri, 31 Dec 9999 23:59:59 GMT`
    } else if (fbclid.startsWith('gl.')) {
      // Google 归因
      const gcl = `GCL.${Math.floor(new Date().getTime() / 1000)}.${fbclid.split('.')[1]}`
      document.cookie = `_gcl_aw=${gcl}; domain=${
        import.meta.env.VITE_COOKIE_DOMAIN
      }; path=/; expires=Fri, 31 Dec 9999 23:59:59 GMT`
    }
  }
}

watch(
  () => accountStore.userId,
  (userId) => {
    if (userId) {
      metrics.setUserId(userId)
    }
  },
  { immediate: true }
)
watch(
  () => accountStore.nickname,
  () => {
    if (accountStore.nickname) {
      metrics.userProperties()
    }
  },
  { immediate: true }
)
watch(
  () => accountStore.memberInfo,
  () => {
    metrics.userProperties()
  },
  { deep: true }
)

const { initTrigger } = useTrigger()

onMounted(async () => {
  await setupI18n()
  setInterval(() => {
    checkNetwork()
  }, 5000)

  getActionByUrl()

  generateDeviceFingerprint().then(async (deviceFingerprint) => {
    console.info('deviceFingerprint', deviceFingerprint)
    globalStore.setTriggerData(await initTrigger())
    console.info('triggerData', globalStore.triggerData)

    // 一般情况下使用设备指纹作为 clientId
    let clientId = deviceFingerprint

    if (IS_INTL) {
      await getClidByIp()

      // 判断 Cookie 中是否存在 fbclid 或者 gbclid 如果不存在，从服务端获取 fbclid，并存入 Cookie 中，有效期3个月
      const fbCookie = document.cookie.split('; ').find((row) => row.startsWith('_fbc='))
      console.info('_fbc cookie', fbCookie)
      const gbCookie = document.cookie.split('; ').find((row) => row.startsWith('_gcl_aw='))
      console.info('_gcl_aw cookie', gbCookie)
      if (!fbCookie && !gbCookie) {
        const { fbclid } = await getFbclid(deviceFingerprint)
        if (fbclid) {
          if (fbclid.startsWith('fb.')) {
            // Facebook 归因
            const fbc = `fb.1.${new Date().getTime()}.${fbclid.split('.')[1]}`
            document.cookie = `_fbc=${fbc}; domain=${
              import.meta.env.VITE_COOKIE_DOMAIN
            }; path=/; expires=Fri, 31 Dec 9999 23:59:59 GMT`
          } else if (fbclid.startsWith('gl.')) {
            // Google 归因
            const gcl = `GCL.${Math.floor(new Date().getTime() / 1000)}.${fbclid.split('.')[1]}`
            document.cookie = `_gcl_aw=${gcl}; domain=${
              import.meta.env.VITE_COOKIE_DOMAIN
            }; path=/; expires=Fri, 31 Dec 9999 23:59:59 GMT`
          }
          // 使用 fbclid 作为 clientId
          clientId = fbclid.split('.')[1]
        }
      } else if (fbCookie) {
        // 如果 _fbc 存在，获取 fbclid ,使用 fbclid 作为 clientId
        const fbclid = fbCookie.split('=')[1].split('.').pop()
        if (fbclid) {
          clientId = fbclid
        }
      } else if (gbCookie) {
        // 如果 _gcl_aw 存在，获取 gbclid ,使用 gbclid 作为 clientId
        const gbclid = gbCookie.split('=')[1].split('.').pop()
        if (gbclid) {
          clientId = gbclid
        }
      }
    }

    getClidByIpAction()
    getActionByFingerprint(deviceFingerprint)

    console.info('clientId', clientId)

    metrics.setClientId(clientId)
  })

  if (!isElectron() && IS_INTL) {
    let consent = getConsentState()
    if (!consent) {
      consent = metrics.configConsent()
      setConsentState(consent)
    }

    if (consent === 'denied') {
      setConsentState(metrics.configConsent())
      showConsentTips.value = true
    } else if (consent === 'granted') {
      setConsentState(metrics.consentGranted())
    }
  }

  if (IS_INTL) {
    initFirebase()
  } else {
    initApmPlus()
  }

  window.addEventListener('resize', setCssProperty)
  setCssProperty()
  showBrowserTips.value = !isSupportBrowser() && !isElectron()
  if (isElectron()) {
    // @ts-ignore
    globalStore.setAppVersion(await electronAPI.getAppVersion())
    console.info('当前版本', globalStore.appVersion)
    // @ts-ignore
    electronAPI.showAboutDialog((_event, arg) => {
      console.info('showAboutDialog', arg)
      showAboutDialog.value = true
    })
    // @ts-ignore
    electronAPI.checkingUpdate((_event, arg) => {
      console.info('checkingUpdate', arg)
      globalStore.setIsCheckingUpdate(arg)
    })
    // @ts-ignore
    globalStore.setIsCheckingUpdate(await electronAPI.getCheckingUpdate())
    // @ts-ignore
    electronAPI.logHandler((_event, arg) => {
      console.info(arg)
    })
    // @ts-ignore
    electronAPI.exceptionHandler((_event, arg) => {
      console.error(arg)
    })
    // @ts-ignore
    electronAPI.infoHandler((_event, arg) => {
      console.info(arg)
    })
    // @ts-ignore
    electronAPI.updateAvailable(() => {
      console.log('自动检测更新')
      globalStore.setHasUpdate(true)
    })
    // @ts-ignore
    electronAPI.envInfo((_event, arg) => {
      globalStore.setEnvInfo({
        type: 'app',
        version: arg.version,
        os: arg.os
      })
    })
    // @ts-ignore
    electronAPI.updateListener(checkUpdateCallback)
    // @ts-ignore
    electronAPI.bonjourChecker((_event, arg) => {
      console.info(arg)
      globalStore.setRequireBonjour(arg)
    })
    // @ts-ignore
    electronAPI.checkBonjour()

    // @ts-ignore
    electronAPI.checkOSVersion()

    // @ts-ignore
    electronAPI.osVersionNotSupport((_event, arg) => {
      console.info('当前操作系统不支持', arg)
      // @ts-ignore
      electronAPI.showDialog({
        type: 'warning',
        title: t(I18nKeys.global.tipsTitle),
        message:
          arg === 'darwin'
            ? t(I18nKeys.system.osVersionNotSupportedMessageMacOS)
            : t(I18nKeys.system.osVersionNotSupportedMessageWindows),
        buttonText: t(I18nKeys.global.confirmButtonText)
      })
    })

    // @ts-ignore
    electronAPI.uploadDownloaded(() => {
      console.info('更新下载完成，即刻安装更新')
      // @ts-ignore
      electronAPI.updateOnLoad({
        type: 'info',
        title: t(I18nKeys.global.tipsTitle),
        message: t(I18nKeys.system.updatedDownloadMessage),
        buttonText: t(I18nKeys.global.ok)
      })
    })

    // @ts-ignore
    electronAPI.ga4EventListener((_event, arg) => {
      metrics.event(arg.eventName, arg.eventParams).fire()
    })

    // @ts-ignore
    electronAPI.refreshUserInfo(() => {
      if (
        router.currentRoute.value.name !== 'update' &&
        router.currentRoute.value.name !== 'update-error' &&
        router.currentRoute.value.name !== 'update-progress'
      ) {
        refreshUserInfo()
      }
    })

    // 每两小时检查一次更新
    setTimeout(
      () => {
        // @ts-ignore
        electronAPI.checkUpdate()
      },
      2 * 60 * 60 * 1000
    )

    // @ts-ignore
    const chromeExist = await electronAPI.chromeExist()
    console.log('chromeExist', chromeExist)
    globalStore.setChromeExist(chromeExist)

    bus.on(EmitKeys.SHOW_ABOUT, () => (showAboutDialog.value = true))
  } else {
    // 浏览器
    const browser = Bowser.parse(window.navigator.userAgent)
    console.log('浏览器名称: ' + browser.browser.name!)
    console.log('浏览器版本: ' + browser.browser.version!)
    console.log('操作系统: ' + browser.os.name!)
    globalStore.setEnvInfo({
      type: 'browser',
      name: browser.browser.name!,
      version: browser.browser.version!,
      os: browser.os.name!
    })
  }

  document.title = t(I18nKeys.global.indexTitle)

  // 拦截豆包扩展器组件
  const blockDoubaoComponent = () => {
    // 移除豆包组件
    const removeDoubaoComponent = () => {
      // 通过标签名和属性组合查找
      const doubaoComponents = document.getElementsByTagName('doubao-ai-csui')
      Array.from(doubaoComponents).forEach((element) => {
        element.remove()
      })

      // 查找并移除翻译助手元素
      document
        .querySelectorAll('[id*="doubao-ai-translate-image-assistant"]')
        .forEach((element) => {
          element.remove()
        })

      // 通过ID模式查找
      const idPattern = /^[0-9a-f]{32}$/
      document.querySelectorAll('[id]').forEach((element) => {
        if (idPattern.test(element.id)) {
          element.remove()
        }
      })

      // 通过 data-host-id 和 data-collection-img-host-id 属性查找
      document
        .querySelectorAll('[data-host-id], [data-collection-img-host-id]')
        .forEach((element) => {
          element.remove()
        })
    }

    // 使用 MutationObserver 监听 DOM 变化
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1) {
            // 元素节点
            const element = node as Element
            // 检查是否是豆包组件
            if (
              element.tagName.toLowerCase() === 'doubao-ai-csui' ||
              element.id?.includes('doubao-ai-translate-image-assistant') ||
              (element.id && /^[0-9a-f]{32}$/.test(element.id)) ||
              element.hasAttribute('data-host-id') ||
              element.hasAttribute('data-collection-img-host-id')
            ) {
              element.remove()
            }
          }
        })
      })
    })

    // 监听整个文档的变化
    observer.observe(document.documentElement, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['id', 'data-host-id', 'data-collection-img-host-id']
    })

    // 立即执行一次移除
    removeDoubaoComponent()
    // 定期检查（以防重新注入）
    setInterval(removeDoubaoComponent, 100)
  }

  // 执行拦截
  blockDoubaoComponent()
})
</script>

<template>
  <NConfigProvider :locale="locale" :date-locale="dateZhCN" :theme-overrides="themeOverrIdes">
    <NEl class="browser-tips" v-if="showBrowserTips">
      {{ $t(I18nKeys.system.browserTips)
      }}<a @click="download">{{ $t(I18nKeys.global.downloadClient) }}</a>
      <NIcon :size="26" @click="showBrowserTips = false">
        <IosClose></IosClose>
      </NIcon>
    </NEl>
    <div class="network-error-tips-container">
      <NEl v-if="showNetworkTips" class="network-error-tips">
        {{ $t(I18nKeys.system.networkErrorMessage) }}
      </NEl>
      <NEl v-if="!serverAvailable" class="network-error-tips">
        {{ $t(I18nKeys.system.serverErrorMessage) }}
      </NEl>
    </div>
    <!-- 开启全局对话框调用 -->
    <NDialogProvider>
      <!-- 开启全局消息提醒调用 -->
      <NMessageProvider>
        <!-- 开启全局通知调用 -->
        <NNotificationProvider>
          <RouterView #default="{ Component }">
            <RouterTransition>
              <component :is="Component"></component>
            </RouterTransition>
          </RouterView>
        </NNotificationProvider>
      </NMessageProvider>
    </NDialogProvider>
    <NGlobalStyle />
    <NEl v-if="!isElectron() && IS_INTL && showConsentTips" class="consent-tips">
      <div class="consent-content">
        <div class="consent-tips-text">
          <div class="consent-tips-title">{{ $t(I18nKeys.system.consentTitle) }}</div>
          <div
            class="consent-tips-content"
            v-html="$t(I18nKeys.system.consentTips)"
            @click="showCookiePolisyDialog = true"
          ></div>
        </div>
        <div class="consent-tips-buttons">
          <NButton type="primary" size="large" @click="acceptConsent">
            {{ $t(I18nKeys.global.accept) }}
          </NButton>
          <NButton size="large" @click="showConsentTips = false">
            {{ $t(I18nKeys.global.reject) }}
          </NButton>
        </div>
      </div>
    </NEl>
    <CookieNotice v-if="showCookiePolisyDialog" @close="showCookiePolisyDialog = false" />
    <AboutDialog v-if="showAboutDialog" @close="showAboutDialog = false" />
  </NConfigProvider>
</template>

<style lang="scss" scoped>
.browser-tips {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  padding: 10px;
  text-align: center;
  z-index: 100;
  background-color: var(--primary-color-hover);
  line-height: 28px;

  .n-icon {
    float: right;
    cursor: pointer;
  }
}

.network-error-tips-container {
  position: fixed;
  top: 40px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 2000;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
}

.network-error-tips {
  padding: 8px 16px;
  background-color: var(--error-color);
  color: #ffffff;
  z-index: 2000;
  font-size: 16px;
}

.consent-tips {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 100;
  background-color: var(--base-color);
  text-align: left;
  font-size: 16px;
  line-height: 28px;
  box-shadow: 0 0 10px 0 rgba(0, 0, 0, 0.1);

  .consent-content {
    max-width: 1280px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 20px;
    margin: 0 auto;
    padding: 30px 60px;
    box-sizing: border-box;
  }

  .consent-tips-text {
    .consent-tips-title {
      font-size: 20px;
      font-weight: 600;
      color: var(--text-color-base);
      margin-bottom: 10px;
    }
  }

  .consent-tips-buttons {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;

    .n-button {
      width: 200px;
      font-size: 16px;
    }
  }
}

.n-config-provider {
  height: 100%;
}

.app-icon {
  position: relative;
  // top: -50px; /* 向上移动20px */
  display: inline-block;
}
</style>
<style lang="scss">
#nprogress .bar {
  background-color: #18a058;
}
</style>
